import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { strToColor } from '@reflact/tsutil';
import { AdminService } from '@shared-angular/services/admin.service';
import { LoginService } from '@shared-angular/services/login.service';
import { ChatbotConfig, Project, User } from '@shared/index';
import dayjs from 'dayjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MenuItem as ActionItems, ConfirmationService, SortEvent } from 'primeng/api';
import { Button, ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { Popover } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ProjectPagehearderComponent } from '../../../../../project-pagehearder/project-pagehearder.component';
import { BotIconComponent } from '../../../../../shared/components/bot-icon/bot-icon.component';
import { BotService } from '../../../../../shared/services/bot.service';
import { RecentBotsService } from '../../../../../shared/services/recent-bots.service';
import { ProjectNamePipe } from '../../../../../shared/pipes/project-name.pipe';
import { ProjectService } from '../../../../../shared/services/project.service';
import { DeleteProjectModalComponent } from './delete-project-modal/delete-project-modal.component';
import { EditProjectModalComponent } from './edit-project-modal/edit-project-modal.component';
import { ImportProjectModalComponent } from './import-project-modal/import-project-modal.component';
import { TransferProjectModalComponent } from './transfer-project-modal/transfer-project-modal.component';

@Component({
  selector: 'app-project-overview',
  imports: [RouterLink, NgTemplateOutlet,
    FormsModule,
    ProjectNamePipe,
    ConfirmPopupModule,
    ButtonModule,
    TableModule,
    TooltipModule,
    Popover,
    ConfirmDialogModule,
    BotIconComponent,
    ProjectPagehearderComponent
  ],
  templateUrl: './project-overview.component.html',
  styleUrl: './project-overview.component.scss',
  standalone: true,
  providers: [ConfirmationService]
})
export class ProjectOverviewComponent implements OnInit {
  public recentBots: ChatbotConfig[] = [];
  public readonly imageVersion = Date.now();
  public users: User<string, string>[] = [];
  public readonly newProjectTrigger = output();
  public sortField: string = 'created';
  public sortOrder: 1 | -1 = -1;
  public hoveredFavoriteProjectId: string | null = null;
  public isHeaderStarActive = false;

  public readonly headerStarShowAllTooltip = $localize`:@@project-header-star-tooltip-show-all:Alle Projekte anzeigen`;
  public readonly headerStarShowFavoritesTooltip = $localize`:@@project-header-star-tooltip-show-favorites:Nur Favoriten anzeigen`;

  public actionsMenu: (ActionItems & { command: () => void })[] = [];
  private activePop: Popover | null = null;
  public dayjs = dayjs;
  private readonly cdr = inject(ChangeDetectorRef);
  constructor(
    public projectService: ProjectService,
    public botService: BotService,
    private recentBotsService: RecentBotsService,
    public loginService: LoginService,
    private modalService: BsModalService,
    private adminService: AdminService,
    private confirmationService: ConfirmationService
  ) {
    const sort = this.projectService.readSortPreference();
    this.sortField = sort.field;
    this.sortOrder = sort.order;
  }

  public async ngOnInit(): Promise<void> {
    const [users, recentBots] = await Promise.all([
      this.adminService.getUsers(), this.recentBotsService.load()
    ]);
    this.users = users;
    this.recentBots = recentBots;
    this.projectService.creatorNames = new Map(users.map(user => [user._id, user.name]));
    this.projectService.sortProjects(this.sortField, this.sortOrder);
    this.cdr.markForCheck();
  }

  public openEditProject(project: Project) {
    const modalRef = this.modalService.show(EditProjectModalComponent, { initialState: { project }, class: 'modal-lg' });
    modalRef.onHidden?.subscribe(async () => {
      await this.projectService.loadProjects();
    });
  }

  public openDeleteProject(project: Project) {
    const modalRef = this.modalService.show(DeleteProjectModalComponent, { initialState: { project } });
    modalRef.onHidden?.subscribe(async () => {
      await this.projectService.loadProjects();
    });
  }

  public openTransferProject(project: Project) {
    const modalRef = this.modalService.show(TransferProjectModalComponent, { initialState: { project, users: this.users } });
    modalRef.onHidden?.subscribe(async () => {
      await this.projectService.loadProjects();
    });
  }

  public openImportProject() {
    this.modalService.show(ImportProjectModalComponent, { class: 'modal-lg', ignoreBackdropClick: true });
  }

  public openExportProject(project: Project) {
    this.confirmationService.confirm({
      header: $localize`:@@export-project-header:Projekt exportieren`,
      message: $localize`:@@export-project-message:Sollen die Project-Secrets im Export enthalten sein? Sie werden dabei verschlüsselt. Wählst du "Ohne Projektgeheimnisse", werden alle Secret-Werte im Export geleert – nach einem Import müssen sie manuell ergänzt werden.`,
      icon: 'ri-download-2-line',
      rejectButtonProps: {
        label: $localize`:@@export-project-without-secrets:Ohne Projektgeheimnisse exportieren`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@export-project-with-secrets:Mit Projektgeheimnissen exportieren`
      },
      accept: () => {
        void this.projectService.exportProjectZIP(project, true);
      },
      reject: () => {
        void this.projectService.exportProjectZIP(project, false);
      }
    });
  }

  public strToColor(str: string): string {
    return strToColor(str);
  }

  public getUserNameById(userId: string) {
    const user = this.users.find(u => u._id === userId);
    return user?.name ?? $localize`:@@user-not-found:Nutzer nicht gefunden`;
  }

  public openMenu(event: MouseEvent, popover: Popover, project: Project, button: Button) {
    if (this.activePop && this.activePop !== popover) {
      this.activePop.hide();
    }
    this.actionsMenu = this.getMenuItems(project);
    popover.toggle(event, button.el.nativeElement);
    this.activePop = popover;
  }

  public canShowMenu(project: Project): boolean {
    if (project.editMode === 'everyone' || project.editMode === 'creator') {
      return true;
    }
    return this.isProjectCreator(project);
  }

  private isProjectCreator(project: Project): boolean {
    return project.creator === this.loginService.loggedInUser?._id;
  }

  public sortProjects(event: SortEvent): void {
    const field = typeof event.field === 'string' ? event.field : 'created';
    const order = event.order === 1 ? 1 : -1;
    // p-table (customSort) meldet bei jeder neuen [value]-Referenz erneut; ohne Guard -> neues Array -> Endlosschleife (NG0103).
    if (field === this.sortField && order === this.sortOrder) return;
    this.sortField = field;
    this.sortOrder = order;
    this.projectService.sortProjects(this.sortField, this.sortOrder);
    this.projectService.saveSortPreference(this.sortField, this.sortOrder);
  }

  public toggleFavoriteProject(project: Project): void {
    void this.projectService.toggleFavoriteProject(project._id);
  }

  public isFavoriteProject(project: Project): boolean {
    return this.projectService.isFavoriteProject(project._id);
  }

  // Stabile Referenz fuer p-table [value], sonst neues Array pro Durchlauf -> NG0103.
  private visibleProjectsCache?: { source: Project[]; org?: string; favoritesOnly: boolean; result: Project[] };

  public visibleProjects(): Project[] {
    const source = this.projectService.projects;
    const org = this.projectService.searchOrgShort;
    const cache = this.visibleProjectsCache;
    if (cache?.source === source && cache.org === org && cache.favoritesOnly === this.isHeaderStarActive) {
      return cache.result;
    }
    const projects = this.projectService.projectsForSelectedOrg();
    const result = this.isHeaderStarActive ? projects.filter(project => this.isFavoriteProject(project)) : projects;
    this.visibleProjectsCache = { source, org, favoritesOnly: this.isHeaderStarActive, result };
    return result;
  }

  public setHoveredFavoriteProject(projectId: string | null): void {
    this.hoveredFavoriteProjectId = projectId;
  }

  public toggleHeaderStarActive(): void {
    this.isHeaderStarActive = !this.isHeaderStarActive;
  }

  private getMenuItems(project: Project) {
    const actionItemsList: (ActionItems & { command: () => void })[] = [];
    // In read-only projects the creator may only change the edit mode back or export
    const readOnly = project.editMode === 'none';

    actionItemsList.push({
      label: $localize`:@@edit:Bearbeiten`,
      icon: 'ri ri-pencil-fill',
      command: () => {
        this.openEditProject(project);
      }
    });
    if (!readOnly) {
      actionItemsList.push({
        label: $localize`:@@transfer:Übertragen`,
        icon: 'ri ri-user-shared-line',
        command: () => {
          this.openTransferProject(project);
        }
      });
    }
    actionItemsList.push({
      label: $localize`:@@export:Exportieren`,
      icon: 'ri ri-download-2-line',
      command: () => {
        this.openExportProject(project);
      }
    });
    if (!readOnly) {
      actionItemsList.push({
        label: $localize`:@@delete:Löschen`,
        icon: 'ri ri-delete-bin-line',
        command: () => {
          this.confirmationCheck(project);
        }
      });
    }
    return actionItemsList;
  }

  public confirmationCheck(project: Project) {
    this.confirmationService.confirm({
      header: $localize`:@@delete-project-confirmation:Projekt löschen`,
      message: $localize`:@@confirmation:Möchtest du dieses Projekt wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      icon: 'ri-error-warning-line',
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: () => {
        this.openDeleteProject(project);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }
}
